import { Chart as ChartJS, TooltipItem } from 'chart.js';
import theme from 'theme';
interface Sensor {
  mainBox?: { unit?: string };
  chart?: { views?: ChartView[] };
}

// Define a type for chart view
interface ChartView {
  title?: string;
  range?: string;
  data?: { labelShort?: string; labelLong?: string; value?: number; unit?: string }[];
}

/**
 * Background plugin for bar charts
 * Creates light gray background bars
 */
export const backgroundBar = {
  id: 'backgroundBar',
  beforeDraw: (chart: ChartJS<'bar'>) => {
    const {
      data,
      ctx,
      chartArea: { top, width, height },
      scales: { x },
    } = chart;
    const length = data?.labels?.length ?? 0;
    ctx.save();
    const segment = width / length;
    const barWidth = segment * 0.49;
    ctx.fillStyle = '#EEF1F5';
    ctx.beginPath();
    for (let i = 0; i < length; i++) {
      ctx.roundRect(x.getPixelForValue(i) - barWidth / 2, top, barWidth, height, 5);
    }
    ctx.fill();
  },
};

/**
 * Gradient background plugin for line charts
 * Creates a gradient fill under the line
 */
export const lineChartGradientBackground = {
  id: 'lineChartGradientBackground',
  beforeDatasetsDraw: (chart: ChartJS<'line'>) => {
    const { ctx, chartArea } = chart;
    //const dataset = chart.data.datasets[0];

    // Remove the type check since we're ensuring it's a line chart when we pass it
    // if (!dataset || dataset.type !== 'line') return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.data.length === 0) return;

    ctx.save();

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, 'rgba(85, 31, 255, 0.4)');
    gradient.addColorStop(1, 'rgba(85, 31, 255, 0)');

    ctx.beginPath();

    const points = meta.data;
    if (!points || points.length === 0) return;

    // Start at the bottom left
    ctx.moveTo(points[0].x, chartArea.bottom);

    // Draw line to each point
    points.forEach((point) => {
      ctx.lineTo(point.x, point.y);
    });

    // Complete the path back to the bottom right
    ctx.lineTo(points[points.length - 1].x, chartArea.bottom);
    ctx.closePath();

    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();
  },
};

/**
 * Creates bar chart options with tooltips
 * @param sensor The sensor data
 * @param selectedTitle The selected chart view title
 * @returns Bar chart options object
 */
export const createBarChartOptions = (sensor: Sensor, selectedTitle: string | null) => ({
  scales: {
    x: {
      ticks: {
        color: theme.colors?.gray?.[7],
      },
      grid: {
        drawBorder: false,
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      ticks: {
        color: theme.colors?.gray?.[7],
      },
      grid: {
        drawBorder: false,
        display: false,
      },
    },
  },
  plugins: {
    tooltip: {
      enabled: true,
      callbacks: {
        title: function (context: TooltipItem<'bar'>[]) {
          if (!sensor || !sensor.chart || !sensor.chart.views) return '';
          const chartViews = sensor.chart.views.find((view: ChartView) => view.title === selectedTitle);
          if (!chartViews || !chartViews.data || context.length === 0) return '';
          const index = context[0].dataIndex;
          const data = chartViews.data[index];
          const value = data.value ?? 0;
          const suffix = sensor.mainBox?.unit ?? '';
          return `${value} ${suffix}`;
        },
        label: function (context: TooltipItem<'bar'>) {
          if (!sensor || !sensor.chart || !sensor.chart.views) return '';
          const chartViews = sensor.chart.views.find((view: ChartView) => view.title === selectedTitle);
          if (!chartViews || !chartViews.data) return '';
          const index = context.dataIndex;
          const data = chartViews.data[index];
          const date = data.labelLong ?? 'N/A';
          return `${date}`;
        },
      },
    },
    legend: {
      display: false,
    },
  },
});

/**
 * Creates line chart options with tooltips
 * @param sensor The sensor data
 * @param selectedTitle The selected chart view title
 * @returns Line chart options object
 */
export const createLineChartOptions = (sensor: Sensor, selectedTitle: string | null) => ({
  pointBackgroundColor: '#FFFFFF',
  pointBorderColor: '#000000',
  pointBorderWidth: 3,
  pointRadius: 4,
  pointHoverRadius: 6,
  fill: true,
  elements: {
    line: {
      borderWidth: 4,
      tension: 0.5,
      borderColor: '#551FFF',
    },
  },
  scales: {
    x: {
      ticks: {
        color: theme.colors?.gray?.[7],
      },
      grid: {
        drawBorder: false,
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      ticks: {
        color: theme.colors?.gray?.[7],
      },
      grid: {
        drawBorder: false,
        display: false,
      },
    },
  },
  plugins: {
    tooltip: {
      enabled: true,
      callbacks: {
        title: function (context: TooltipItem<'line'>[]) {
          if (!sensor || !sensor.chart || !sensor.chart.views) return '';
          const chartViews = sensor.chart.views.find((view: ChartView) => view.title === selectedTitle);
          if (!chartViews || !chartViews.data || context.length === 0) return '';
          const index = context[0].dataIndex;
          const data = chartViews.data[index];
          const value = data.value ?? 0;
          const suffix = sensor.mainBox?.unit ?? '';
          return `${value} ${suffix}`;
        },
        label: function (context: TooltipItem<'line'>) {
          if (!sensor || !sensor.chart || !sensor.chart.views) return '';
          const chartViews = sensor.chart.views.find((view: ChartView) => view.title === selectedTitle);
          if (!chartViews || !chartViews.data) return '';
          const index = context.dataIndex;
          const data = chartViews.data[index];
          const date = data.labelLong ?? 'N/A';
          return `${date}`;
        },
      },
    },
    legend: {
      display: false,
    },
  },
});
